/**
 * ChatMarkdown
 *
 * ReactMarkdown wrapper for chat messages. Handles internal links
 * (e.g., /reflections/:id?block=N) using React Router navigation instead
 * of full page reloads, and renders GitHub-flavoured markdown — tables,
 * strikethrough, task lists, autolinks — via remark-gfm.
 *
 * Without remark-gfm the model's pipe tables are not parsed at all: they fall
 * through to a single paragraph of literal `|` characters, which on a phone
 * reads as an unbroken wall of text.
 */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface ChatMarkdownProps {
  children: string;
}

export function ChatMarkdown({ children }: ChatMarkdownProps) {
  const navigate = useNavigate();

  const handleLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
      // Only intercept internal links (starting with /)
      if (href.startsWith("/")) {
        e.preventDefault();
        navigate(href);
      }
    },
    [navigate]
  );

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ href, children: linkChildren, ...props }) => {
          const url = href || "";
          if (url.startsWith("/")) {
            return (
              <a
                href={url}
                onClick={(e) => handleLinkClick(e, url)}
                className="text-primary underline hover:text-primary/80 cursor-pointer"
                {...props}
              >
                {linkChildren}
              </a>
            );
          }
          // External links open in new tab
          return (
            <a href={url} target="_blank" rel="noopener noreferrer" {...props}>
              {linkChildren}
            </a>
          );
        },
        /*
          Tables get explicit styling rather than relying on the `prose` class:
          ChatMarkdown is also rendered outside a typography container, and a
          borderless table is barely distinguishable from prose.

          The wrapper scrolls, not the table. A `min-w` keeps columns readable
          instead of letting a 3-column table crush to 40px each on a phone —
          the overflow is contained here so the page itself never gains a
          horizontal scrollbar.
        */
        table: ({ children: tableChildren, ...props }) => (
          <div
            className="my-3 w-full overflow-x-auto"
            data-testid="table-scroll"
          >
            <table
              className="w-full min-w-[26rem] border-collapse text-left text-sm"
              {...props}
            >
              {tableChildren}
            </table>
          </div>
        ),
        thead: ({ children: headChildren, ...props }) => (
          <thead className="bg-foreground/5" {...props}>
            {headChildren}
          </thead>
        ),
        th: ({ children: thChildren, ...props }) => (
          <th
            className="border border-border px-3 py-2 align-top font-semibold"
            {...props}
          >
            {thChildren}
          </th>
        ),
        td: ({ children: tdChildren, ...props }) => (
          <td className="border border-border px-3 py-2 align-top" {...props}>
            {tdChildren}
          </td>
        ),
      }}
    >
      {children}
    </ReactMarkdown>
  );
}
